import { SkeletonLoading } from '@saleshandy/design-system';
import React from 'react';
import { isNull } from 'lodash';
import { useSelector } from 'react-redux';
import { Checkbox, Send, Users } from '@saleshandy/icons';
import SequenceListTableFallback from './sequence-list-table-fallback';
import { RootState } from '../../../../store/root-reducer';
import {
  ProfileProgressSteps,
  UserSettingCode,
} from '../../../../shared/types/user-setting';
import OnboardingProgressFallback from './onboarding-progress-fallback';
import { EmailAccount } from '../../../../shared/svg';

const SequenceListFallback: React.FC = () => {
  const { profileProgress, meta, isAgency } = useSelector(
    (state: RootState) => state.home,
  );
  const isAgencyUser = !!parseInt(isAgency, 10);

  const areAllStepsCompleted = (): boolean => {
    if (profileProgress) {
      return profileProgress.every((step) => step.isCompleted);
    }

    return true;
  };

  const isSkippedOnboarding = (): boolean => {
    if (Array.isArray(meta) && meta.length > 0) {
      const skipOnboarding = meta.find(
        (setting) => setting.code === UserSettingCode.SkipOnboarding,
      );
      if (skipOnboarding) {
        return skipOnboarding.value === '1';
      }
    }
    return true;
  };

  const onboardingFirstSectionItems = [
    {
      id: 1,
      icon: <Send width={16} height={16} className="popover-arrow-color-txt" />,
      label: 'Create New Sequence',
      stepName: ProfileProgressSteps.CreateSequence,
    },
    {
      id: 2,
      icon: (
        <Users width={16} height={16} className="popover-arrow-color-txt" />
      ),
      label: 'Add Prospects',
      stepName: ProfileProgressSteps.AddProspects,
    },
    {
      id: 3,
      icon: <EmailAccount width={16} height={16} />,
      label: 'Add Email Account',
      stepName: ProfileProgressSteps.ConnectEmail,
    },
    {
      id: 4,
      icon: (
        <Checkbox width={16} height={16} className="popover-arrow-color-txt" />
      ),
      label: 'Launch Sequence',
      stepName: ProfileProgressSteps.ActivateSequence,
    },
  ];

  enum ProgressStepSection {
    FirstSection = 'first-section',
    SecondSection = 'second-section',
  }

  const getProfileProgressSection = () => {
    // 1. Check if any items in the first section are pending (isCompleted is false)
    const hasPendingFirstSection = onboardingFirstSectionItems.some(
      (actionItem) => {
        const matchingProgress = profileProgress.find(
          (progress) =>
            progress.profileProgressStep?.stepName === actionItem.stepName,
        );
        return matchingProgress && !matchingProgress.isCompleted;
      },
    );

    // 2. Check if any remaining items outside of the first section are pending
    const hasPendingSecondSection = profileProgress.some(
      (progress) =>
        !onboardingFirstSectionItems.some(
          (actionItem) =>
            actionItem.stepName === progress.profileProgressStep?.stepName,
        ) && !progress.isCompleted,
    );

    // Logic for deciding which section to show
    if (hasPendingFirstSection) {
      return ProgressStepSection.FirstSection; // Show the first section if it has pending items
    }
    if (hasPendingSecondSection) {
      return ProgressStepSection.SecondSection; // Show the second section if it has pending items
    }

    return false;
  };

  const shouldShowOnboardingProgressLoader =
    !areAllStepsCompleted() &&
    !isNull(profileProgress) &&
    !isSkippedOnboarding();
  return (
    <div className="sequence-components">
      <div className="sequence-list-container">
        {shouldShowOnboardingProgressLoader && (
          <OnboardingProgressFallback
            profileProgressSection={getProfileProgressSection()}
            isAgencyUser={isAgencyUser}
          />
        )}
        {/* Header Section */}
        <div className="sequence-filter-bar skeleton-height-1">
          <div className="sequence-filter-bar-left">
            <SkeletonLoading width={202} height={32} />
            <SkeletonLoading width={126} height={32} />
            <SkeletonLoading width={124} height={32} />
          </div>
          <div>
            <SkeletonLoading width={147} height={32} />
          </div>
        </div>

        {/* Table Section */}
        <SequenceListTableFallback />
      </div>
    </div>
  );
};

export default SequenceListFallback;
